import React, { useCallback, useMemo } from 'react';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useAppSelector } from 'store/hooks';
import { showContinueInMobileAlert } from 'features/pots/utils';
import { assertUnreachable } from 'utils/types';
import * as selectors from '../hooks/selectors';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { isDesktop } from '../../../constants';
import { selectAutoInvestPlanForAccount, selectHasAutoInvest, selectTradingAccount } from '../reducers/selectors';
import HowToBuyModal from './HowToBuyModal';
const strings = {
    buy: 'Buy',
    sell: 'Sell',
    recurringBuy: 'Set recurring order',
    passcodeTitle: 'Enter Your Pin Code',
    biometryDescriptionOperation: {
        buy: 'buying',
        sell: 'selling',
    },
};
const Button = (props) => {
    const { assetId, flex, gray } = props;
    const styles = useStyles(styleSet);
    const navigateToNextScreen = useNavigateToNextScreen();
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const { navigateDeprecated, popToTop } = useMainStackNavigation();
    const tradingAccount = useAppSelector(selectTradingAccount);
    const status = tradingAccount?.account.status;
    const accountId = tradingAccount?.account.id;
    const hasAutoInvest = useAppSelector(selectHasAutoInvest);
    const hasFetchedAutoinvestPlans = useAppSelector((state) => !!state.invest.autoInvestPlans);
    const autoInvestPlan = useAppSelector((state) => selectAutoInvestPlanForAccount(state, accountId));
    const isAssetInAutoinvest = useMemo(() => autoInvestPlan?.allocations.some((a) => a.assetId === assetId), [autoInvestPlan, assetId]);
    const requiredUserAction = selectors.useRequiredUserAction();
    const onBuy = useCallback(() => {
        if (scrambled || !status)
            return;
        switch (status) {
            case 'NOT_STARTED':
                navigateToNextScreen();
                break;
            case 'IDENTITY_CHECKS_IN_PROGRESS':
            case 'OPENING_IN_PROGRESS':
            case 'NOT_ELIGIBLE':
            case 'CLOSING_IN_PROGRESS':
            case 'CLOSED':
            case 'FROZEN':
            case 'JISA_CONVERTED':
            case 'JISA_TRANSFER_REQUIRED':
                popToTop();
                break;
            case 'IDENTITY_CHECKS_NEED_USER_ACTION': {
                if (isDesktop) {
                    showContinueInMobileAlert();
                }
                else {
                    switch (requiredUserAction) {
                        case 'UPLOAD_IDENTITY_DOCUMENT':
                            navigateDeprecated('WealthDocumentUploadIntroScreen', {
                                documentType: 'POI',
                            });
                            break;
                        case 'UPLOAD_PROOF_OF_ADDRESS':
                            navigateDeprecated('WealthDocumentUploadIntroScreen', {
                                documentType: 'POA',
                            });
                            break;
                        default:
                            // Should never get here, but who knows.
                            // There is no strict rule that says that if status is 'IDENTITY_CHECKS_NEED_USER_ACTION' then requiredUserAction will be defined
                            popToTop();
                    }
                }
                break;
            }
            case 'ACTIVE':
                if (assetId) {
                    if (hasAutoInvest &&
                        hasFetchedAutoinvestPlans &&
                        (!isAssetInAutoinvest || autoInvestPlan?.status === 'paused') &&
                        !props.recurring) {
                        HowToBuyModal.show(assetId, () => {
                            navigateDeprecated('InvestBuyCustomAmount', {
                                assetId,
                                recurring: props.recurring,
                            });
                        });
                    }
                    else {
                        navigateDeprecated('InvestBuyCustomAmount', {
                            assetId,
                            recurring: props.recurring,
                        });
                    }
                }
                break;
            default:
                // all statuses should be handled above. If new status is added - typesciprt will warn us
                assertUnreachable(status);
        }
    }, [
        scrambled,
        status,
        navigateToNextScreen,
        assetId,
        requiredUserAction,
        hasAutoInvest,
        hasFetchedAutoinvestPlans,
        isAssetInAutoinvest,
        autoInvestPlan?.status,
        props.recurring,
    ]);
    const onSell = useCallback(() => {
        if (scrambled)
            return;
        if (assetId) {
            navigateDeprecated('BeforeYouSell', { assetId });
        }
    }, [scrambled, assetId]);
    const colors = useColors();
    const title = useMemo(() => {
        const color = (() => {
            if ('sell' in props) {
                return colors.text.textOnBrand;
            }
            if ('gray' in props) {
                return colors.text.secondary;
            }
            return colors.text.whiteOnColor;
        })();
        return (<Text Text-16 color={color}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {'buy' in props ? ('recurring' in props ? strings.recurringBuy : strings.buy) : strings.sell}
      </Text>);
    }, [colors.text.secondary, colors.text.textOnBrand, colors.text.whiteOnColor, props]);
    if ('buy' in props) {
        return (<TouchableBounce style={[styles.container, styles.buy, flex && styles.flex, gray && styles.gray]} onPress={onBuy}>
        {title}
      </TouchableBounce>);
    }
    if (status !== 'ACTIVE') {
        return null;
    }
    return (<TouchableBounce style={[styles.container, styles.sell, flex && styles.flex]} onPress={onSell}>
      {title}
    </TouchableBounce>);
};
/**
 * The height of the Button
 */
export const HEIGHT = 56;
const styleSet = createStyleSheets((colors) => ({
    container: {
        height: rem(HEIGHT),
        borderRadius: rem(56),
        alignItems: 'center',
        justifyContent: 'center',
    },
    flex: {
        flex: 1,
    },
    buy: {
        backgroundColor: colors.buttons.brand,
    },
    sell: {
        backgroundColor: colors.buttons.brandGroup,
    },
    gray: {
        backgroundColor: colors.buttons.secondary,
    },
}));
export default Button;
